import { Button, Stack } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { getEquipmentColumnsFromAssemblyTemplate } from '@/utils';
import * as state from '@/components/Analysis/state';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { ColumnsSelectionDialog } from '../ColumnsSelectionDialog';
import { ExportCsvSubheading } from '../InsightsExplorer/ExportCsvSubheading';
import {
  useGetAssembliesByTemplateFieldsFiltersForEquipmentExportLazyQuery,
  useGetStructureNameLazyQuery,
  Assembly,
  ColumnKeysEnum,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useEquipmentData } from '@/hooks/useEquipmentData';
import { useRouter } from '@/hooks';
import { format } from 'date-fns';
import { Columns } from '@/types';
import { useUnitConversion } from '@/hooks/useUnitConversion';
import ExcelJS from 'exceljs';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import * as customBlockState from '@/components/Analysis/modules/CustomBlocks/state';
import { useUserPreferences } from '@/hooks/useUserPreferences';

const staticColumnHeaders = [
  {
    key: 'tagName',
    columnName: 'Component',
    visibility: true,
    isSortable: false,
  },
] as Columns[];

export const EquipmentExportXlsx = ({ title = 'Export Excel' }: { title?: string }) => {
  const assemblyTemplate = useAtomValue(state.assemblyTemplate);
  const selectedTemplateId = useAtomValue(state.selectedAssemblyTemplateId)!;
  const selectedTemplateFilters = useAtomValue(state.selectedTemplateFiltersState);
  const allSelectedPaintRegionIds = useAtomValue(state.allSelectedPaintRegionIds);
  const selectedDeckIds = useAtomValue(state.selectedFilteredDeckIds);
  const structureLocations = useAtomValue(state.structureLocations);
  const selectedSavedBlock = useAtomValue(customBlockState.selectedSavedBlock);
  const structureId = useGetStructureId();
  const { userColumns, refetchUserPreferences } = useUserPreferences(
    structureId,
    ColumnKeysEnum.Component
  );
  const { getCellValue } = useEquipmentData();
  const { getUnitSuffix } = useUnitConversion();
  const { showSnackBar } = useSnackBarMessage({});

  const router = useRouter();
  const { inspection } = router.query;

  const [fetchFilteredAssemblies] =
    useGetAssembliesByTemplateFieldsFiltersForEquipmentExportLazyQuery({
      variables: {
        input: {
          selectedTemplateFilters,
          structureId,
          templateId: selectedTemplateId,
          selectedPaintRegionIds: [
            ...allSelectedPaintRegionIds,
            ...(selectedSavedBlock?.paintRegion?.id ? [selectedSavedBlock.paintRegion.id] : []),
          ],
          selectedDeckIds,
        },
        pageInfo: {
          sortBy: 'createdAt',
          sortDir: 1,
        },
      },
    });
  const [getStructureName] = useGetStructureNameLazyQuery({
    variables: { structureId: (inspection as string) || '' },
    fetchPolicy: 'cache-first',
  });

  const templateColumns = useMemo(() => {
    if (!assemblyTemplate) return [];
    return getEquipmentColumnsFromAssemblyTemplate(assemblyTemplate);
  }, [assemblyTemplate]);

  const dialogColumns = useMemo(() => {
    const updatedColumns = [...userColumns];

    if (!updatedColumns.includes('Component')) {
      updatedColumns.push('Component');
    }

    const selectableColumns = [...staticColumnHeaders, ...templateColumns].map(
      ({ columnName, key, displayUnit, unit, templateFieldKey, displayConfig }) => ({
        key,
        name: columnName,
        isSelected: updatedColumns.includes(columnName),
        displayUnit: displayUnit || '',
        unit: unit || '',
        templateFieldKey,
        displayConfig,
      })
    );
    return [
      ...selectableColumns,
      {
        key: '3dViewURL',
        name: '3D View URL',
        isSelected: updatedColumns.includes('3D View URL'),
        displayUnit: '',
        unit: '',
        templateFieldKey: '',
        displayConfig: undefined,
      },
    ];
  }, [templateColumns, userColumns]);

  const [dialogOpen, setDialogOpen] = useState(false);

  const handleClick = () => {
    refetchUserPreferences();
    setDialogOpen(true);
  };

  const foundViewpoint = useCallback(
    (key: string) => {
      return structureLocations?.find((location) => location.id === key)?.name ?? 'N/A';
    },
    [structureLocations]
  );

  const handleExportXlsx = useCallback(
    async (selectedColumnsKeys: string[]) => {
      try {
        const [structureNameQueryData, assembliesData] = await Promise.all([
          getStructureName(),
          fetchFilteredAssemblies(),
        ]);

        const structureName = structureNameQueryData?.data?.structure?.name;
        const assemblies = assembliesData?.data?.getAssembliesByTemplateFieldsFilters ?? [];

        // Create a workbook
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Assemblies');

        // Prepare column headers
        const headers: string[] = [];
        const columnData: { key: string; label: string; active: boolean }[] = [];

        // Add static columns
        staticColumnHeaders.forEach(({ columnName, key }) => {
          if (selectedColumnsKeys.includes(key)) {
            headers.push(columnName);
            columnData.push({ key, label: columnName, active: true });
          }
        });

        // Add dynamic columns
        templateColumns.forEach((column) => {
          const { columnName, key, unit, displayUnit, templateFieldKey } = column;
          if (selectedColumnsKeys.includes(key)) {
            const unitSuffix = getUnitSuffix({
              unit: displayUnit ?? unit,
              field: templateFieldKey,
            });
            const label = unitSuffix ? `${columnName} (${unitSuffix})` : columnName;
            headers.push(label);
            columnData.push({ key, label, active: true });
          }
        });

        // Add 3D View URL column if selected
        if (selectedColumnsKeys.includes('3dViewURL')) {
          headers.push('3D View URL');
          columnData.push({ key: '3dViewURL', label: '3D View URL', active: true });
        }

        // Add header row
        const headerRow = worksheet.addRow(headers);
        headerRow.eachCell((cell) => {
          cell.font = { bold: true, name: 'Arial' };
          cell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
        });

        // Add data rows
        assemblies.forEach((assembly) => {
          const rowData: string[] = [];

          columnData.forEach(({ key, active }) => {
            if (!active) return;

            // Handle static columns
            if (key === 'componentId') {
              rowData.push(assembly?.id ?? '');
              return;
            }
            if (key === 'tagName') {
              rowData.push(assembly?.tagName ?? '');
              return;
            }

            if (key === '3dViewURL') {
              const url = `${
                window.location.origin
              }/analysis/${structureId}/viewer?assembly=${encodeURIComponent(assembly.tagName)}`;
              rowData.push(url);
              return;
            }

            // Handle dynamic columns
            const column = templateColumns.find((col) => col.key === key);
            if (column) {
              const { displayConfig, columnName } = column;
              const { value, subValue } = getCellValue(assembly as Assembly, column);

              let cellValue = subValue ? `${value} (${subValue})` : value;

              // Handle special cases
              if (displayConfig?.type === 'LINK' && cellValue !== 'N/A') {
                cellValue = window.location.origin + cellValue;
              } else if (columnName === 'Governing Blister Viewpoint' && cellValue !== 'N/A') {
                cellValue = foundViewpoint(cellValue);
              }

              rowData.push(cellValue);
            }
          });

          const row = worksheet.addRow(rowData);

          // Add hyperlink to the 3D View URL cell if present
          const urlIdx = columnData.findIndex((col) => col.key === '3dViewURL');
          if (urlIdx !== -1 && rowData[urlIdx]) {
            row.getCell(urlIdx + 1).value = { text: '3D View', hyperlink: rowData[urlIdx] };
            row.getCell(urlIdx + 1).font = {
              color: { argb: 'FFFF0000' },
              underline: true,
              name: 'Arial',
            };
          }

          row.eachCell((cell) => {
            cell.font = { name: 'Arial' };
            cell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
          });
          row.height = 20;
        });

        // Auto-size columns
        worksheet.columns.forEach((column, index) => {
          const headerWidth = (headers[index]?.toString() || '').length;
          const maxDataWidth = Math.max(
            ...assemblies.map((_, rowIndex) => {
              const cell = worksheet.getCell(rowIndex + 2, index + 1);
              return (cell.value?.toString() || '').length;
            })
          );
          const columnWidth = Math.min(Math.max(headerWidth, maxDataWidth, 10) + 2, 50);
          column.width = columnWidth;
        });

        // Generate filename with date
        const date = format(new Date(), 'dd-MMM-yyyy');
        const fileName = `${structureName}-${date}.xlsx`;

        // Generate and download the file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();

        window.URL.revokeObjectURL(url);

        showSnackBar('Query result has been exported successfully', 'success');
      } catch (error) {
        console.error('Export error:', error);
        showSnackBar('Failed to export Excel', 'error');
      }
    },
    [
      fetchFilteredAssemblies,
      foundViewpoint,
      getCellValue,
      getStructureName,
      getUnitSuffix,
      showSnackBar,
      structureId,
      templateColumns,
    ]
  );

  return (
    <Stack>
      <ColumnsSelectionDialog
        open={dialogOpen}
        columns={dialogColumns}
        heading="Customize Table Columns"
        subHeading={<ExportCsvSubheading />}
        applyButtonText="Download"
        applyButtonLoadingText="Downloading..."
        successMessage="Query result has been exported successfully"
        errorMessage="Failed to export Excel"
        onApply={handleExportXlsx}
        onClose={() => setDialogOpen(false)}
      />

      <Button
        id="export-equipment-xlsx"
        onClick={handleClick}
        startIcon={<AbyssIcon iconName="excel" style={{ height: '1.6rem', width: '1.6rem' }} />}
        variant="outlined"
        size="small"
      >
        {title}
      </Button>
    </Stack>
  );
};
